/**
 * AskUserQuestions — a stepped question flow (one question at a time, "Question
 * X of Y"). Single- or multi-select options, an optional always-visible free
 * text ("Other") input, Skip/Next controls, and an onComplete callback after
 * the last question. Controlled or uncontrolled for both the active index and
 * the answers map.
 *
 * Faithful to the documented contract: options carry {id,title,description};
 * single-select auto-advances on click; multiSelect (or allowOther) adds a Next
 * button; answers are keyed by question id and expose {selectedIds, otherText,
 * skipped}. Composes @excellent/ui atoms — no bespoke primitives.
 *
 * Role split vs `SetupDriver` (the other card walk — deliberate, not drift):
 * SetupDriver owns setup/action walks with optimistic persistence + lifecycle
 * signals; this block owns pure Q&A flows with a controlled answers map (the
 * workflows builder reads it live). They share one chrome contract — the
 * Back/counter header and `SegmentedProgress` (imported from setup-driver) —
 * so the two read as one family. Don't build a third stepper.
 */

import * as React from "react";
import { cn } from "../utils";
import { Card, CardContent } from "../card";
import { Button } from "../button";
import { Input } from "../input";
import { Icon } from "../icon";
import { SectionHeading } from "./section-heading";
import { SegmentedProgress } from "./setup/setup-driver";

export interface AskUserOption {
  /** Stable id returned in the answer. Falls back to position. */
  id?: string;
  /** Bold leading label. */
  title: string;
  /** Secondary muted text shown after / under the title. */
  description?: string;
}

export interface AskUserQuestion {
  /** Stable id used to key the answer. Falls back to position. */
  id?: string;
  /** Question text shown above the options. */
  title: string;
  /** Optional helper text under the question title. */
  hint?: string;
  /** 2–5 options to choose from. */
  options: AskUserOption[];
  /** Allow multiple selections. Adds a Next button. */
  multiSelect?: boolean;
  /** Render an always-visible inline text input for free-form answers. */
  allowOther?: boolean;
  otherPlaceholder?: string;
  /** Show the Skip control in the header. Default true. */
  skippable?: boolean;
  /** Label for the Next button (multi-select / allowOther). Default Next/Finish. */
  nextLabel?: string;
  /** Row layout. "stacked" puts the description on its own line. Default inline. */
  layout?: "inline" | "stacked";
}

export interface AskUserAnswer {
  questionId: string;
  /** Selected option ids. 0–1 in single-select, 0–N in multi-select. */
  selectedIds: string[];
  /** Free-form text from the Other input, if any. */
  otherText?: string;
  /** True when the user skipped the question. */
  skipped?: boolean;
}

export interface AskUserQuestionsProps {
  /** Ordered questions. 2–5 options per question recommended. */
  questions: AskUserQuestion[];
  /** Controlled active question index. */
  currentIndex?: number;
  defaultCurrentIndex?: number;
  onCurrentIndexChange?: (index: number) => void;
  /** Controlled answers map keyed by question id. */
  answers?: Record<string, AskUserAnswer>;
  defaultAnswers?: Record<string, AskUserAnswer>;
  onAnswersChange?: (answers: Record<string, AskUserAnswer>) => void;
  /** Called after the last question is answered or submitted. */
  onComplete?: (answers: Record<string, AskUserAnswer>) => void;
  /** Called when the user clicks Skip on a question. */
  onSkip?: (questionId: string, index: number) => void;
  skipLabel?: string;
  className?: string;
}

const qid = (q: AskUserQuestion, i: number) => q.id ?? String(i);
const oid = (o: AskUserOption, i: number) => o.id ?? String(i);

function useControllable<T>(
  controlled: T | undefined,
  defaultValue: T,
  onChange?: (v: T) => void,
): [T, (v: T) => void] {
  const [internal, setInternal] = React.useState<T>(defaultValue);
  const isControlled = controlled !== undefined;
  const value = isControlled ? (controlled as T) : internal;
  const set = React.useCallback(
    (v: T) => {
      if (!isControlled) setInternal(v);
      onChange?.(v);
    },
    [isControlled, onChange],
  );
  return [value, set];
}

export function AskUserQuestions({
  questions,
  currentIndex,
  defaultCurrentIndex = 0,
  onCurrentIndexChange,
  answers,
  defaultAnswers,
  onAnswersChange,
  onComplete,
  onSkip,
  skipLabel = "Skip",
  className,
}: AskUserQuestionsProps) {
  const total = questions.length;
  const [index, setIndex] = useControllable(currentIndex, defaultCurrentIndex, onCurrentIndexChange);
  const [answerMap, setAnswerMap] = useControllable(answers, defaultAnswers ?? {}, onAnswersChange);

  const safeIndex = Math.min(Math.max(index, 0), Math.max(total - 1, 0));
  const q = questions[safeIndex];
  const id = q ? qid(q, safeIndex) : "";
  const current = answerMap[id];

  // Draft selection for the active question (committed on advance). Seeded from
  // any existing answer so Back/forward keeps prior choices.
  const [selected, setSelected] = React.useState<string[]>(current?.selectedIds ?? []);
  const [otherText, setOtherText] = React.useState<string>(current?.otherText ?? "");

  // Re-seed the draft whenever the active question changes.
  React.useEffect(() => {
    const a = answerMap[id];
    setSelected(a?.selectedIds ?? []);
    setOtherText(a?.otherText ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!q) return null;

  const isLast = safeIndex === total - 1;
  const skippable = q.skippable ?? true;
  const needsExplicitNext = Boolean(q.multiSelect || q.allowOther);
  const layout = q.layout ?? "inline";

  const commitAndAdvance = (override?: string[]) => {
    const sel = override ?? selected;
    const ans: AskUserAnswer = {
      questionId: id,
      selectedIds: sel,
      otherText: q.allowOther ? otherText.trim() || undefined : undefined,
      skipped: false,
    };
    const nextMap = { ...answerMap, [id]: ans };
    setAnswerMap(nextMap);
    if (isLast) onComplete?.(nextMap);
    else setIndex(safeIndex + 1);
  };

  const toggle = (optionId: string) => {
    if (q.multiSelect) {
      setSelected((s) => (s.includes(optionId) ? s.filter((x) => x !== optionId) : [...s, optionId]));
    } else if (needsExplicitNext) {
      // single-select but Next is required (allowOther): just set selection.
      setSelected([optionId]);
    } else {
      // single-select, no Other: select + auto-advance.
      setSelected([optionId]);
      commitAndAdvance([optionId]);
    }
  };

  const skip = () => {
    const ans: AskUserAnswer = { questionId: id, selectedIds: [], skipped: true };
    const nextMap = { ...answerMap, [id]: ans };
    setAnswerMap(nextMap);
    onSkip?.(id, safeIndex);
    if (isLast) onComplete?.(nextMap);
    else setIndex(safeIndex + 1);
  };

  const back = () => setIndex(Math.max(0, safeIndex - 1));

  const canSubmit = selected.length > 0 || (q.allowOther && otherText.trim().length > 0);
  const nextText = q.nextLabel ?? (isLast ? "Finish" : "Next");

  // Keyboard: 1–9 selects the nth option; Enter submits when a Next is shown.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      if (e.key === "Enter" && needsExplicitNext && canSubmit) {
        e.preventDefault();
        commitAndAdvance();
      }
      return;
    }
    const n = Number(e.key);
    if (Number.isInteger(n) && n >= 1 && n <= q.options.length) {
      e.preventDefault();
      toggle(oid(q.options[n - 1]!, n - 1));
    } else if (e.key === "Enter" && needsExplicitNext && canSubmit) {
      e.preventDefault();
      commitAndAdvance();
    }
  };

  return (
    <Card className={className}>
      <CardContent className="flex flex-col gap-4 py-5">
        {/* Header: progress + skip */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {safeIndex > 0 && (
              <button
                type="button"
                onClick={back}
                className="flex items-center gap-1 type-subtext text-muted-foreground hover:text-foreground"
                aria-label="Previous question"
              >
                <Icon name="arrow-left" size="xs" /> Back
              </button>
            )}
            <span className="type-subtext font-medium text-muted-foreground">
              Question {safeIndex + 1} of {total}
            </span>
          </div>
          {skippable && (
            <button
              type="button"
              onClick={skip}
              className="type-subtext text-muted-foreground hover:text-foreground"
            >
              {skipLabel}
            </button>
          )}
        </div>

        {/* Progress — the shared card-walk affordance */}
        <SegmentedProgress count={total} index={safeIndex} />

        {/* Question — re-keyed so it animates in on change */}
        <div
          key={id}
          onKeyDown={onKeyDown}
          className="flex flex-col gap-3 duration-200 animate-in fade-in-0 slide-in-from-bottom-1"
        >
          <SectionHeading size="md" as="h3" title={q.title} description={q.hint} />

          <div className="flex flex-col">
            {q.options.map((o, i) => {
              const optionId = oid(o, i);
              const active = selected.includes(optionId);
              return (
                // button-ignore: selectable answer option (list-row, aria-pressed), not an action button
                <button
                  type="button"
                  key={optionId}
                  onClick={() => toggle(optionId)}
                  aria-pressed={active}
                  className={cn(
                    "group flex items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors",
                    "border-b border-border/40 first:rounded-t-md last:rounded-b-md",
                    active
                      ? "border-primary bg-selected"
                      : "border-transparent hover:bg-selected/50",
                  )}
                >
                  {/* selection indicator */}
                  <span
                    className={cn(
                      "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center border text-primary-foreground transition-colors",
                      q.multiSelect ? "rounded-xs" : "rounded-full",
                      active ? "border-primary bg-primary" : "border-border bg-transparent",
                    )}
                  >
                    {active && <Icon name="check" size="xs" className="h-3 w-3" />}
                  </span>
                  <span className={cn("flex min-w-0 flex-1 gap-1.5", layout === "stacked" ? "flex-col" : "flex-row flex-wrap items-baseline")}>
                    <span className="type-body font-medium text-foreground">{o.title}</span>
                    {o.description && (
                      <span className="type-body text-muted-foreground">{o.description}</span>
                    )}
                  </span>
                  <span className="ml-auto shrink-0 self-center type-subtext tabular-nums text-muted-foreground opacity-0 group-hover:opacity-100">
                    {i + 1}
                  </span>
                </button>
              );
            })}
          </div>

          {q.allowOther && (
            <Input
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              placeholder={q.otherPlaceholder ?? "Describe in your own words…"}
              aria-label="Other"
            />
          )}

          {(needsExplicitNext || isLast) && (
            <div className="flex items-center gap-3 pt-1">
              <Button onClick={() => commitAndAdvance()} disabled={!canSubmit}>
                {nextText}
                {!isLast && <Icon name="arrow-right" size="sm" className="ml-1" />}
              </Button>
              {q.multiSelect && (
                <span className="type-subtext text-muted-foreground">
                  {selected.length} selected{q.allowOther && otherText.trim() ? " + 1 custom" : ""}
                </span>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
